import { apiFetch } from "./client";
import type {
  AvailableLegalEntitiesResponse,
  ContractCountsResponse,
  ContractApprovePayload,
  ContractAttachmentUploadPayload,
  ContractCreatePayload,
  ContractDetailResponse,
  ContractListQuery,
  ContractListResponse,
  ContractMainDocumentPayload,
  ContractMarkSignedPayload,
  ContractRenewPayload,
  ContractReturnPayload,
  ContractTermsUpdatePayload,
  PriceTypesResponse,
} from "./types";

export function fetchContracts(
  q: ContractListQuery = {},
  signal?: AbortSignal,
): Promise<ContractListResponse> {
  return apiFetch<ContractListResponse>("/Custom/frontend/contracts", {
    query: {
      view: q.view,
      search: q.search || undefined,
      cursor: q.cursor,
      limit: q.limit,
      offset: q.offset || undefined,
      source: q.source || undefined,
      statusGroup: q.statusGroup || undefined,
      kormaxLe: q.kormaxLe || undefined,
      usageStatus: q.usageStatus || undefined,
      // D-140 W6 — only send when narrowing away from the default exclude.
      archived: q.archived && q.archived !== "exclude" ? q.archived : undefined,
    },
    signal,
  });
}

/**
 * WS-1 — the ONE canonical counts snapshot (AC-WS-SOURCE-SNAPSHOT): every
 * KPI / tab / source-chip / archive-chip number the workspace renders.
 */
export function fetchContractCounts(signal?: AbortSignal): Promise<ContractCountsResponse> {
  return apiFetch<ContractCountsResponse>("/Custom/frontend/contracts/counts", { signal });
}

export function fetchContractDetail(
  id: string,
  signal?: AbortSignal,
): Promise<ContractDetailResponse> {
  return apiFetch<ContractDetailResponse>(
    `/Custom/frontend/contracts/${encodeURIComponent(id)}`,
    { signal },
  );
}

export function createContract(
  body: ContractCreatePayload,
): Promise<ContractDetailResponse> {
  return apiFetch<ContractDetailResponse>("/Custom/frontend/contracts", {
    method: "POST",
    body,
  });
}

/**
 * G2-S2 (D2=C) — legal entities selectable by the current user for a NEW
 * contract. Server-side flag-gated (contract_le_availability_filter): OFF =
 * all live LEs, ON = availability-filtered.
 */
export function fetchAvailableLegalEntities(
  signal?: AbortSignal,
): Promise<AvailableLegalEntitiesResponse> {
  return apiFetch<AvailableLegalEntitiesResponse>(
    "/Custom/frontend/contracts/legal-entities/available",
    { signal },
  );
}

/** Contract UNIFICATION — BI price-type reference for the draft terms select. */
export function fetchContractPriceTypes(
  signal?: AbortSignal,
): Promise<PriceTypesResponse> {
  return apiFetch<PriceTypesResponse>("/Custom/frontend/price-types", { signal });
}

/** Contract UNIFICATION — save editable commercial terms on a crm_managed draft. */
export function updateContractTerms(
  id: string,
  body: ContractTermsUpdatePayload,
): Promise<ContractDetailResponse> {
  return apiFetch<ContractDetailResponse>(
    `/Custom/frontend/contracts/${encodeURIComponent(id)}/terms`,
    { method: "POST", body },
  );
}

export function uploadMainDocument(
  id: string,
  body: ContractMainDocumentPayload,
): Promise<{ attachmentId: string; versionLabel: string; documentType: string }> {
  return apiFetch(`/Custom/frontend/contracts/${encodeURIComponent(id)}/main-document`, {
    method: "POST",
    body,
  });
}

export function submitForApproval(id: string): Promise<{ contractId: string; currentState: string }> {
  return apiFetch(`/Custom/frontend/contracts/${encodeURIComponent(id)}/submit-for-approval`, {
    method: "POST",
    body: {},
  });
}

export function approveContract(
  id: string,
  body: ContractApprovePayload,
): Promise<{ contractId: string; currentState: string }> {
  return apiFetch(`/Custom/frontend/contracts/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    body,
  });
}

export function returnContract(
  id: string,
  body: ContractReturnPayload,
): Promise<{ contractId: string; currentState: string }> {
  return apiFetch(`/Custom/frontend/contracts/${encodeURIComponent(id)}/return`, {
    method: "POST",
    body,
  });
}

export function markSignedContract(
  id: string,
  body: ContractMarkSignedPayload,
): Promise<{ contractId: string; currentState: string; attachmentId: string }> {
  return apiFetch(`/Custom/frontend/contracts/${encodeURIComponent(id)}/mark-signed`, {
    method: "POST",
    body,
  });
}

export function renewContract(
  id: string,
  body: ContractRenewPayload,
): Promise<{
  contractId: string;
  mode: string;
  newContractId?: string;
  taskId?: string;
  renewalStatus?: string;
}> {
  return apiFetch(`/Custom/frontend/contracts/${encodeURIComponent(id)}/renew`, {
    method: "POST",
    body,
  });
}

export function uploadContractAttachment(
  id: string,
  body: ContractAttachmentUploadPayload,
): Promise<{ attachmentId: string; documentType: string }> {
  return apiFetch(`/Custom/frontend/contracts/${encodeURIComponent(id)}/attachments`, {
    method: "POST",
    body,
  });
}

/** Returns the URL the browser hits to stream/download. */
export function attachmentDownloadUrl(contractId: string, attachmentId: string): string {
  return `/api/v1/Custom/frontend/contracts/${encodeURIComponent(
    contractId,
  )}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

export function mainDocumentDownloadUrl(contractId: string, documentId: string): string {
  return `/api/v1/Custom/frontend/contracts/${encodeURIComponent(
    contractId,
  )}/documents/${encodeURIComponent(documentId)}/download`;
}

// ── G3-S3 — Contracts Archive Review (visual ④, D-060) ──────────────────────

export type ArchiveReason = "no_ship_12m" | "inactive_le_recent_ship";

export interface ArchiveReviewItem {
  id: string;
  number: string;
  account: string;
  usageStatus: string;
  lastShipmentDate: string | null;
  shipmentCount12m: number;
  turnover12m: number | null;
  reason: ArchiveReason;
  bulkEligible: boolean;
  manualLocked: boolean;
}

export interface ArchiveReviewResponse {
  items: ArchiveReviewItem[];
  total: number;
  bulkEligible: number;
  hasMore: boolean;
}

export function fetchArchiveReview(
  filter: "candidate" | "archived" | "all" = "candidate",
  signal?: AbortSignal,
): Promise<ArchiveReviewResponse> {
  return apiFetch<ArchiveReviewResponse>("/Custom/frontend/contracts/archive-review", {
    query: { filter },
    signal,
  });
}

export function archiveContracts(
  ids: string[],
  bulk: boolean,
): Promise<{ status: string; count: number; rejectedLive: string[] }> {
  return apiFetch("/Custom/frontend/contracts/archive-review/archive", {
    method: "POST",
    body: { ids, bulk },
  });
}

export function keepContracts(ids: string[]): Promise<{ status: string; count: number }> {
  return apiFetch("/Custom/frontend/contracts/archive-review/keep", { method: "POST", body: { ids } });
}

export function unarchiveContract(id: string): Promise<{ status: string; id: string }> {
  return apiFetch("/Custom/frontend/contracts/archive-review/unarchive", { method: "POST", body: { id } });
}
